import { createFileRoute } from "@tanstack/react-router";
import {
  useAppHostRegistry,
  useOptionalAppHostRegistry,
} from "forest/sandbox/AppHostRegistry";
import { AppStage } from "forest/sandbox/AppStage";
import { appAt, hasPage } from "forest/sandbox/use-app-index";
import { X } from "lucide-react";
import type { AppIndexEntry } from "playground/apps/index/app-index";
import { normalizeAppIntegrations } from "playground/apps/manifest";
import { useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";

import { basename } from "../../-route-title";
import { useWorkspace } from "../../-workspace-context";
import { AppInfoBadge } from "../../../apps/AppInfoBadge";
import { AppPermissionGate } from "../../../apps/AppPermissionGate";
import { AppIcon } from "../../../apps/display";
import { TitleBar } from "../../../components/TitleBar";
import { IntegrationGrantBanner } from "../../../integrations/IntegrationGrantBanner";

export const Route = createFileRoute("/_workspace/app/$")({
  head: ({ params }) => {
    const path = params._splat ?? "";
    return { meta: [{ title: path ? basename(path) : "Apps" }] };
  },
  component: AppPage,
});

function AppPage() {
  // `_splat` is the App's directory **path** (the launcher identity, e.g.
  // `Apps/typescript-console` — a splat so its `/`s survive as path segments).
  const appPath = Route.useParams()._splat ?? "";
  const { closeFile } = useWorkspace();
  const registry = useOptionalAppHostRegistry();

  if (!registry) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
        Sandbox is still initializing…
      </div>
    );
  }

  return <ReadyAppPage appPath={appPath} onClose={closeFile} />;
}

function ReadyAppPage({
  appPath,
  onClose,
}: {
  appPath: string;
  onClose: () => void;
}) {
  const { apps, compact } = useWorkspace();
  // `appAt` (not an exact-path lookup) so a link into an App's interior still
  // lands on the owning App — the directory is opaque, its internals aren't
  // addressable surfaces.
  const entry = appAt(apps.entries, appPath);
  const registry = useAppHostRegistry();
  const displayName = entry?.displayName ?? basename(appPath);

  // The header X is an *explicit close*: dispose the persistent host (so it
  // launches fresh next time), then navigate away. Plain navigation elsewhere
  // leaves the host running in the background.
  const handleClose = () => {
    registry.closeApp(entry?.path ?? appPath);
    onClose();
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TitleBar
        actions={
          <>
            {/* The (i) badge is on every App title bar — built-ins included
                (§ 3.4): permissions + risk tier, immediately left of close. */}
            {entry?.id ? <AppInfoBadge appId={entry.id} /> : null}
            <button
              type="button"
              onClick={handleClose}
              className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground sm:h-7 sm:w-7"
              title="Close"
              aria-label="Close app"
            >
              <X className="h-4 w-4" />
            </button>
          </>
        }
      >
        {/* The compact layout shows the panel button instead; two adjacent
            icons (one a button, one not) read as clutter, so drop the app icon
            when compact. */}
        {!compact ? (
          <AppIcon
            icon={entry?.icon}
            className="h-4 w-4 shrink-0 text-muted-foreground"
          />
        ) : null}
        <span className="truncate text-sm font-medium">{displayName}</span>
      </TitleBar>
      <AppBody appPath={appPath} entry={entry} loaded={apps.loaded} />
    </div>
  );
}

interface AppBodyProps {
  appPath: string;
  /** The indexed App at (or containing) `appPath`, if any. */
  entry: AppIndexEntry | undefined;
  /** Whether the app index has finished its initial catch-up. */
  loaded: boolean;
}

function AppBody({ appPath, entry, loaded }: AppBodyProps) {
  const registry = useAppHostRegistry();
  // Bumped when a member approves the App's permissions: the running host's CSP,
  // guard, and capability providers were all scoped at launch, so the App
  // relaunches (fresh spec + token) to pick the widened scope up — the key
  // remounts the stage.
  const [grantEpoch, setGrantEpoch] = useState(0);
  // Until the index's catch-up resolves we can't know whether the App is
  // installed — a not-yet-found entry is indistinguishable from a genuinely
  // missing one. Hold at loading so the "isn't installed" message only shows
  // once we're sure.
  if (!entry && !loaded) {
    return (
      <div className="flex-1 p-4">
        <Skeleton className="h-full w-full" />
      </div>
    );
  }
  if (!entry) {
    return (
      <div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-muted-foreground">
        App <span className="mx-1 font-mono">{appPath}</span> isn't installed in
        this workspace.
      </div>
    );
  }
  // A present-but-broken manifest: the directory is still an App (it shows in
  // listings), just not launchable — surface the authoring error instead.
  if (!entry.manifest) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center text-sm">
        <p className="text-muted-foreground">
          This app's <span className="font-mono">playground.json</span> is
          invalid, so it can't launch.
        </p>
        {entry.error ? (
          <p className="max-w-xl font-mono text-xs text-destructive">
            {entry.error}
          </p>
        ) : null}
      </div>
    );
  }
  // An App that declares integrations gets a connect banner above its surface —
  // empty until the running App actually asks for an account (an explicit
  // `connect()`, or a call that found none). Declaring is not itself a prompt.
  // `declared` supplies each provider's manifest `reason` for the ask.
  const declared = normalizeAppIntegrations(entry.manifest.capabilities);
  // Every App (built-in or workspace, page or not) renders through the
  // permission gate: while a capability is unapproved the gate shows its
  // placeholder/dialog instead of the surface. That must include a PAGE-LESS
  // App — a tools-only App still asks for consent (a high-risk one can only
  // be approved through this dialog), and this route is the one member surface
  // it's guaranteed to have. Only a page-bearing App mounts the persistent
  // host: the iframe is parked off-screen and staged here once the gate
  // passes, so an unapproved host is never launched.
  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {declared.length > 0 && (
        <IntegrationGrantBanner declared={declared} appPath={entry.path} />
      )}
      <AppPermissionGate
        appId={entry.id ?? entry.path}
        onApproved={() => {
          registry.closeApp(entry.path);
          setGrantEpoch((e) => e + 1);
        }}
      >
        {hasPage(entry) ? (
          <AppStage
            key={grantEpoch}
            appPath={entry.path}
            className="flex-1 bg-muted/30"
          />
        ) : (
          <div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-muted-foreground">
            This app doesn't have a page.
          </div>
        )}
      </AppPermissionGate>
    </div>
  );
}
